<script setup lang="ts">
import type { BarChartData } from '../index.vue';
import { scaleBand, type ScaleLinear } from 'd3';
import TooltipComponent, { type TooltipData } from '../../tooltip/index.vue';
import { useElementBounding, useMouse, useResizeObserver, useWindowSize } from '@vueuse/core';

// ================================================ Composables
const { y } = useMouse();
const { getTailwindColor } = useTailwind();

// ================================================ Props
const emit = defineEmits<{
  (e: 'xAxisValues', value: HTMLElement[]): void;
  (e: 'hover-point' | 'click-point', index: number): void;
  (e: 'unhover-point'): void;
}>();
const props = defineProps<{
  data: BarChartData[];
  loading: boolean;
  yScale: ScaleLinear<number, number>;
  clickable: boolean;
  tooltip?: (dataIndex: number) => TooltipData | undefined;
}>();

// ================================================ Data
const colors = [getTailwindColor('emerald-500'), '#e7deff'];
const numBars = computed(() => props.data[0]?.barValues.length ?? 0);
const delayPerBar = computed(() => 500 / totalBars.value);
const PX_BETWEEN_BARS = 5;
const isVisible = ref(false);
const tooltipData = ref<TooltipData | null>(null);
const tooltipPosition = ref({
  show: false,
  barGroupIndex: 0,
  indicatorLeftPosition: 0,
  left: 0,
  top: 0,
});

const containersBarRef = ref<HTMLElement[]>([]);
const responsiveBarWidth = ref(0);
const xAxisValues = ref<HTMLElement[]>([]);

// ================================================ Tooltip
const tooltipPortal = ref<HTMLElement | null>(null);
const tooltipRef = ref<HTMLElement | null>(null);

// ================================================ Methods
const showTooltip = (groupBarIndex: number) => {
  if (!tooltipPortal.value || !props.tooltip) return;

  const containerBarRef = containersBarRef.value[groupBarIndex];
  if (!containerBarRef) return;

  const { left, top, width, update } = useElementBounding(containerBarRef);
  const { width: windowWidth } = useWindowSize();

  const tooltipLeft = left.value + 200 / 2 > windowWidth.value ? windowWidth.value - 200 / 2 : left.value + width.value / 2;

  update();
  tooltipPosition.value = {
    show: true,
    barGroupIndex: groupBarIndex,
    indicatorLeftPosition: width.value / 2,
    left: tooltipLeft,
    top: top.value,
  };
  tooltipData.value = props.tooltip(groupBarIndex) ?? null;

  // Emit event for showing point on area
  emit('hover-point', groupBarIndex);
};

const hideTooltip = () => {
  tooltipPosition.value.show = false;

  // Emit event for hiding point on area
  emit('unhover-point');
};

// Responsive dates
useResizeObserver(containersBarRef.value, (entries) => {
  const entry = entries[0];
  if (!entry) return;
  const { width } = entry.contentRect;
  responsiveBarWidth.value = width;
});
const shouldShowLabel = (index: number) => {
  const thresholds = [
    { width: 13, modulo: 1 },
    { width: 10, modulo: 2 },
    { width: 8, modulo: 3 },
    { width: 6, modulo: 4 },
    { width: 4, modulo: 5 },
    { width: 2, modulo: 10 },
    { width: 1, modulo: 20 },
  ];

  for (const { width, modulo } of thresholds) {
    if (responsiveBarWidth.value > width) {
      return index % modulo === 0;
    }
  }

  // Default case for very small widths
  return index % 10 === 0;
};

// ================================================ Computed
const xScale = computed(() =>
  scaleBand()
    .domain(props.data.map((d) => d.key))
    .range([0, 100])
    .padding(0.4),
);

const totalBars = computed(() => {
  if (!props.data.length || !props.data[0]?.barValues?.length) return 1;
  return props.data.length * props.data[0].barValues.length;
});

const barWidth = computed(() => {
  return (100 - PX_BETWEEN_BARS * (numBars.value - 1)) / numBars.value;
});

// ================================================ Lifecycle
watch(
  xAxisValues,
  () => {
    emit('xAxisValues', xAxisValues.value);
  },
  {
    immediate: true,
    deep: true,
  },
);

// Reset animation when data changes
watch(
  () => props.data,
  () => {
    isVisible.value = false;
    setTimeout(() => {
      isVisible.value = true;
    }, 100);
  },
  { deep: true },
);

onMounted(() => {
  setTimeout(() => {
    isVisible.value = true;
  }, 100);

  // Create tooltip portal element
  tooltipPortal.value = document.createElement('div');
  tooltipPortal.value.id = 'chart-tooltip';
  document.body.appendChild(tooltipPortal.value);
});

onBeforeUnmount(() => {
  if (tooltipPortal.value && document.body.contains(tooltipPortal.value)) {
    document.body.removeChild(tooltipPortal.value);
  }
});
</script>
<template>
  <!-- Loading state -->
  <template v-if="loading">
    <div class="absolute z-[20] bottom-0 flex items-center flex-col gap-4 justify-center w-full h-full">
      <ui-loader class="h-10 w-10" />
      <p class="text-gray-400 text-sm">{{ $t('charts.loading_data') }}</p>
    </div>
  </template>

  <!-- Actual Chart -->
  <template v-else>
    <!-- Bars -->
    <div
      v-for="(d, index) in data"
      :key="index"
      ref="containersBarRef"
      class="absolute z-[30] bottom-0 group"
      :style="{
        left: `${xScale(d.key)}%`,
        width: `${xScale.bandwidth()}%`,
        height: '100%',
      }"
      :class="[clickable ? 'hover:cursor-pointer hover:opacity-80' : '']"
      @mouseenter="showTooltip(index)"
      @mouseleave="hideTooltip()"
      @click="emit('click-point', index)"
    >
      <div
        v-for="(value, barIndex) in d.barValues"
        :key="`bar-${index}-${barIndex}`"
        class="chart-bar rounded-t"
        :style="{
          left: `${barIndex * (barWidth + PX_BETWEEN_BARS)}%`,
          width: `${barWidth}%`,
          height: isVisible ? `${100 - yScale(value.value) === 0 ? 4 + 'px' : 100 - yScale(value.value) + '%'}` : '4px',
          backgroundColor: value.color ?? colors[barIndex % colors.length],
          'transition-delay': `${(index * d.barValues.length + barIndex) * delayPerBar}ms`,
          opacity: isVisible ? 1 : 0,
        }"
      />
    </div>

    <!-- Hover bar -->
    <div
      v-for="(d, index) in data"
      :key="`hover-bar-${index}`"
      class="absolute z-[0] bottom-0"
      :style="{
        left: `${xScale(d.key)}%`,
        width: `${xScale.bandwidth()}%`,
        height: '100%',
      }"
    >
      <div
        v-if="tooltipPosition.show && tooltipPosition.barGroupIndex === index"
        class="absolute z-[-1] h-full w-full bg-gray-200/70 rounded border border-dashed border-gray-400 hover:cursor-pointer hover:bg-red-200"
      />
    </div>

    <!-- X Axis Labels -->
    <div
      v-for="(entry, i) in data"
      :key="i"
      class="absolute overflow-visible text-gray-400 mt-2"
      :style="{
        left: `${(xScale(entry.key) ?? 0) + xScale.bandwidth() / 2}%`,
        top: '100%',
        transform: 'rotate(45deg) translateX(4px) translateY(8px)',
        display: shouldShowLabel(i) ? 'block' : 'none',
      }"
    >
      <div ref="xAxisValues" class="absolute text-xs -translate-y-1/2 whitespace-pre">{{ entry.key }}</div>
    </div>
  </template>

  <!-- Tooltip -->
  <transition
    enter-active-class="transition-opacity duration-150"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition-opacity duration-150"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <Teleport v-if="tooltipPosition.show" :to="'body'">
      <TooltipComponent
        ref="tooltipRef"
        class="fixed z-50 pointer-events-none -translate-x-[50%] -translate-y-[120%]"
        :style="{
          top: `${y}px`,
          left: `${tooltipPosition.left}px`,
        }"
        :title="tooltipData?.title"
        :subtitle="tooltipData?.subtitle"
        :body="tooltipData?.body"
        :footer="tooltipData?.footer"
        no-arrow
      />
    </Teleport>
  </transition>
</template>
<style>
.chart-bar {
  transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: bottom;
  will-change: transform;
  position: absolute;
  bottom: 0;
  max-height: 100%;
}
</style>
